import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { RotaCheckbox, RotaButton, useToast } from "@teamrota/rota-design";
import { MEMBER_VENUE_EXCLUSION_TYPES } from "@teamrota/rota-common";
import { useMemberVenues } from "./graphql/get-member-venues";
import { useMemberExclusions } from "./graphql/get-member-exclusions";
import { reasonOptions } from "../../view-edit-account/user-venues/consts";
import useVenueExclusions from "./graphql/manage-venue-exclusions";
import {
  Container,
  Content,
  PartnerSection,
  PartnerName,
  VenueList,
  VenueItem,
  NoVenues
} from "./styles";

const MemberVenues = ({ memberId }) => {
  const [selectedVenueIds, setSelectedVenueIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const {
    venues,
    loading: venuesLoading,
    error: venuesError
  } = useMemberVenues(memberId);
  const {
    excludedVenues,
    loading: exclusionsLoading,
    error: exclusionsError,
    refetch: refetchExclusions
  } = useMemberExclusions(memberId);

  const {
    createOrUpdateMemberVenueExclusion,
    deleteMemberVenueExclusion
  } = useVenueExclusions();

  const { showToast } = useToast();

  // Initialize selectedVenueIds with all non-excluded venues when data is loaded
  useEffect(() => {
    if (venues && excludedVenues && !venuesLoading && !exclusionsLoading) {
      const excludedIds = new Set(
        excludedVenues.map(exclusion => String(exclusion.venueId))
      );
      const initialSelected = new Set(
        venues
          .filter(venue => !excludedIds.has(String(venue.id)))
          .map(venue => venue.id)
      );
      setSelectedVenueIds(initialSelected);
    }
  }, [venuesLoading, exclusionsLoading]);

  const loading = venuesLoading || exclusionsLoading;
  const error = venuesError || exclusionsError;

  if (loading) return <div>Loading venues...</div>;
  if (error) return <div>Error loading venues</div>;

  if (!venues?.length) {
    return <NoVenues>No venues found</NoVenues>;
  }

  const getReasonLabel = reason => {
    const option = reasonOptions.find(opt => opt.value === reason);
    return option ? option.label : reason;
  };

  // Create a set of excluded venue IDs for quick lookup
  const excludedVenueIds = new Set(
    excludedVenues.map(exclusion => String(exclusion.venueId))
  );

  // Group venues by partner and sort by partner name
  const venuesByPartner = venues.reduce((acc, venue) => {
    if (!acc[venue.partnerName]) {
      acc[venue.partnerName] = [];
    }
    const isExcluded = excludedVenueIds.has(String(venue.id));

    acc[venue.partnerName].push({
      ...venue,
      isExcluded,
      exclusion: excludedVenues.find(
        exclusion => String(exclusion.venueId) === String(venue.id)
      )
    });
    return acc;
  }, {});

  const handleVenueToggle = venueId => {
    setSelectedVenueIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(venueId)) {
        newSelected.delete(venueId);
      } else {
        newSelected.add(venueId);
      }
      return newSelected;
    });
  };

  // Sort partner names alphabetically
  const sortedPartners = Object.entries(venuesByPartner).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Find venues that need exclusions added (unchecked venues without exclusions)
      const venuesNeedingExclusion = venues.filter(
        venue =>
          !selectedVenueIds.has(venue.id) &&
          !excludedVenueIds.has(String(venue.id))
      );

      // Find venues that need exclusions removed (checked venues with exclusions)
      const venuesNeedingRemoval = venues.filter(
        venue =>
          selectedVenueIds.has(venue.id) &&
          excludedVenueIds.has(String(venue.id))
      );

      // Group new exclusions by partner
      const exclusionsByPartner = venuesNeedingExclusion.reduce(
        (acc, venue) => {
          if (!acc[venue.partnerId]) {
            acc[venue.partnerId] = [];
          }
          acc[venue.partnerId].push({
            memberId,
            venueId: venue.id,
            reason: MEMBER_VENUE_EXCLUSION_TYPES.EXCLUDED_BY_CANDIDATE
          });
          return acc;
        },
        {}
      );

      // Add new exclusions by partner
      await Promise.all(
        Object.entries(exclusionsByPartner).map(([partnerId, exclusions]) =>
          createOrUpdateMemberVenueExclusion({
            variables: {
              memberVenueExclusions: exclusions,
              partnerId
            }
          })
        )
      );

      // Remove existing exclusions
      await Promise.all(
        venuesNeedingRemoval.map(venue => {
          const exclusion = excludedVenues.find(
            e => String(e.venueId) === String(venue.id)
          );
          return exclusion
            ? deleteMemberVenueExclusion({
                variables: {
                  id: exclusion.id,
                  partnerId: venue.partnerId
                }
              })
            : Promise.resolve();
        })
      );

      // Refetch exclusions to update the UI
      showToast("Changes saved successfully.", { severity: "success" });
      await refetchExclusions();
    } catch (error) {
      console.error("Error saving venue exclusions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <Content>
        {sortedPartners.map(([partnerName, partnerVenues]) => (
          <PartnerSection key={partnerName}>
            <PartnerName>{partnerName}</PartnerName>
            <VenueList>
              {partnerVenues.map(venue => (
                <VenueItem
                  key={venue.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <RotaCheckbox
                    isChecked={selectedVenueIds.has(venue.id)}
                    onClick={() => handleVenueToggle(venue.id)}
                  />
                  <span>{venue.name}</span>
                  {venue.isExcluded && (
                    <span
                      style={{
                        color: "#FF4D4D",
                        fontSize: "14px",
                        fontStyle: "italic"
                      }}
                    >
                      (Excluded: {getReasonLabel(venue.exclusion?.reason)})
                    </span>
                  )}
                </VenueItem>
              ))}
            </VenueList>
          </PartnerSection>
        ))}
      </Content>
      <div style={{ marginTop: "20px", textAlign: "right" }}>
        <RotaButton
          onClick={handleSave}
          isLoading={isSaving}
          disabled={isSaving}
        >
          Save Changes
        </RotaButton>
      </div>
    </Container>
  );
};

MemberVenues.propTypes = {
  memberId: PropTypes.string.isRequired
};

export default MemberVenues;
